import { formatText, TextRole } from '@pickleballinc/lib/format-text';
import { LEAGUE_LABELS } from '@pickleballinc/lib/product-labels';

import { publicEnv } from '@/lib/env';
import { GroupPlayer } from '@/modules/league/new-components/types';

// One of these columns shows on group card for each player row in group when match is started
export const clubRatingColumns: { key: string; label: string; dataKey: keyof GroupPlayer }[] = [
  { key: 'w', label: 'Wins', dataKey: 'TeamWins' },
  { key: 'h2h', label: 'Head to Head Wins', dataKey: 'TeamHeadToHeadWins' },
  { key: 'e', label: 'Points Earned', dataKey: 'TeamPointsEarned' },
  { key: 'a', label: 'Points Against', dataKey: 'TeamPointsAgainst' },
  { key: 'dif', label: 'Difference Earned Against', dataKey: 'TeamDifference_EarnedAgainst' },
  {
    key: 'h2h dif',
    label: 'Head to Head Point Difference Within Head To Head',
    dataKey: 'TeamHeadToHead_PointDifferenceWithinHeadToHead',
  },
  { key: 'l', label: 'Losses', dataKey: 'TeamLosses' },
];

export const factorStatsColumns: { key: string; label: string; dataKey: keyof GroupPlayer }[] = [
  {
    key: 'E/P diff%',
    label: 'Total Points - Earned Possible Percentage',
    dataKey: 'TeamPointDifferencePercentage',
  },
  { key: 'e', label: 'Points Earned', dataKey: 'TeamPointsEarned' },
  { key: 'a', label: 'Points Against', dataKey: 'TeamPointsAgainst' },
  { key: 'P', label: 'Total Points Possible', dataKey: 'TeamPointsPossible' },
  { key: 'w', label: 'Wins', dataKey: 'TeamWins' },
  { key: 'l', label: 'Losses', dataKey: 'TeamLosses' },
];

export const upDownColumns: { key: string; label: string; dataKey: keyof GroupPlayer }[] = [
  { key: 'st', label: 'Standings', dataKey: 'TeamNewSeedNumber' },
  { key: 'w', label: 'Wins', dataKey: 'TeamWins' },
  { key: 'h2h', label: 'Head to Head Wins', dataKey: 'TeamHeadToHeadWins' },
  { key: 'dif', label: 'Difference Earned Against', dataKey: 'TeamDifference_EarnedAgainst' },
  {
    key: 'h2h dif',
    label: 'Head to Head Point Difference Within Head To Head',
    dataKey: 'TeamHeadToHead_PointDifferenceWithinHeadToHead',
  },
  { key: 'l', label: 'Losses', dataKey: 'TeamLosses' },
  { key: 'e', label: 'Points Earned', dataKey: 'TeamPointsEarned' },
  { key: 'a', label: 'Points Against', dataKey: 'TeamPointsAgainst' },
];

// For displaying date in HeaderContent
export const dateOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
  timeZone: 'America/Los_Angeles', // set the timezone to PST
};

// Links for navigation on Game Day pages
export const gameDayLinks = [
  { type: 'rsvp', label: 'RSVPS' },
  { type: 'groups', label: 'Groups' },
  { type: 'matches', label: 'Matches' },
];

// Sort options on RSVP cards
export const SORT_OPTIONS = [
  { label: 'Name A-Z', value: 'asc' },
  { label: 'Name Z-A', value: 'desc' },
  { label: 'Rank', value: 'ranks' },
];

// Csv file for playing list
export const csvHeaders = [
  { label: 'Last Name (1)', key: 'LastName_1' },
  { label: 'First Name (1)', key: 'FirstName_1' },
  { label: 'Last Name (2)', key: 'LastName_2' },
  { label: 'First Name (2)', key: 'FirstName_2' },
];

// Links for all game days
export const allGameDayLinks = [
  { type: 'upcoming', label: 'Upcoming' },
  { type: 'past', label: 'Past' },
];

// Links for all sessions
export const allSessionsLinks = [
  { type: 'current', label: 'Current' },
  { type: 'past', label: 'Past' },
];

// Options for Select on RSVP page - UpDown league Rounds
export const upDownOptions = [
  { value: '1', label: 'Round 1' },
  { value: '2', label: 'Round 2' },
];

// Options for Print score sheets
export const sizeOfSheet = [
  { value: 'half', label: 'Half Sheets' },
  { value: 'full', label: 'Full Sheets' },
];

export const NEXT_PUBLIC_PL_KEY = publicEnv.NEXT_PUBLIC_PL_KEY;

export const MOVED_FROM_WAIT_EMAIL_TEMPLATE_ID = 'd-f67168b414e74c8984e00d621c0d0ce3';
export const COMPLETED_GAME_DAY_EMAIL_TEMPLATE_ID = 'd-b55eb94b6f32402eaf379eccc3e39a31';
export const MOVED_FROM_PLAYING_EMAIL_TEMPLATE_ID = 'd-495f8eb329164decb47f32d135a4c1c4';

export const CONTENT_TYPE_FOR_NOTIFICATION = 'TRANSACTIONAL_CONTENT';

// Marks a partner (doubles) league - either signal, same gate the checkout register page uses.
// The title is matched against PlayerTypeTitle from the API, so it is not UI copy - do not format it.
export const PARTNER_PLAYER_TYPE_ID = 2;
export const PARTNER_PLAYER_TYPE_TITLE = 'Partner';

// Messages for the partner step of Add players, where the player is saved before their partner.
// Read by the server helper and the panel, and rendered as sentence case by both, so they are
// formatted once here.
export const PARTNER_SAVE_FAILED_MESSAGE = formatText({
  text: 'The partner could not be saved.',
  role: TextRole.ErrorMessage,
});
export const PLAYER_ADDED_PARTNER_FAILED_MESSAGE = formatText({
  text: 'The partner could not be saved, so the player is not on the game day yet.',
  role: TextRole.ErrorMessage,
});
// The partner fields saved, but the pair was not finalized onto the game day. Retrying the partner
// step runs the same call again, so it is the way out of this state.
export const GAME_DAY_FINALIZE_FAILED_MESSAGE = formatText({
  text: 'The partner was saved, but the pair could not be added to the game day.',
  role: TextRole.ErrorMessage,
});
export const PARTNER_REGISTRATION_LOAD_FAILED_MESSAGE = formatText({
  text: 'The registration of this player could not be loaded.',
  role: TextRole.ErrorMessage,
});
export const PARTNER_REGISTRATION_NOT_FOUND_MESSAGE = formatText({
  text: 'The registration of this player could not be found.',
  role: TextRole.ErrorMessage,
});

// Debounce before either Add-players search goes out - API_v2_League_SearchNewPlayer for the player
// and API_v2_League_Reg_SearchPartner for the partner. Each is a stored-procedure call of its own,
// so neither runs on every keystroke.
export const ADD_PLAYERS_SEARCH_DEBOUNCE_MS = 300;

// Shortest term either search will send. A one-character term matches most of the player table, so
// the stored procedure scans for seconds and the answer is useless by the time it lands - long
// enough that it was routinely being cancelled and recorded as a failed execution. Both search
// routes enforce the same minimum, so this only keeps the call from going out at all.
export const ADD_PLAYERS_SEARCH_MIN_LENGTH = 2;

export const ROLL_BACK_OPTIONS = [
  {
    id: 'keep',
    label: 'Keep all scores',
    value: false,
  },
  {
    id: 'remove',
    label: 'Remove all scores',
    value: true,
  },
];

// Email and phone reach the cards partly covered ('r*******5@gmail.com', '******3559'). In the body
// font every asterisk run gets a different width, so the two lines never line up; a monospace stack
// renders them evenly. Spelled out as an arbitrary utility because this app's tailwind theme
// replaces fontFamily wholesale, so `font-mono` is not generated here.
export const MASKED_CONTACT_FONT_CLASS =
  '[font-family:ui-monospace,_SFMono-Regular,_Menlo,_Consolas,_monospace]';

// The checkout Find-partner sheet paints every action that picks a partner in a soft pink with a
// deep-rose label, and sits its white cards on an off-white surface. The admin panel mirrors both so
// the two halves of the same flow look alike. The `!` prefixes win over the react-ui Button
// variant's own colours.
export const PARTNER_ACTION_COLOR_CLASSES =
  '!border-[#F4C7CF] !bg-[#F4C7CF] hover:!border-[#ebabb7] hover:!bg-[#ebabb7]';
export const PARTNER_ACTION_LABEL_CLASS = 'text-[#7E2638]';
export const PANEL_SURFACE_BG_CLASS = 'bg-[#F9FAFB]';

// Every card is at least as tall as a complete one, so a row missing a city or a phone leaves no
// shorter card in the list. One height covers both lists now that a partner row draws the same
// detail lines a player row does: 120px of content (a 24px name over five 16px detail lines, with
// the 8px gaps between them) plus the card's own 16px padding top and bottom.
export const SEARCH_CARD_MIN_HEIGHT_CLASS = 'min-h-[152px]';

// Shown under the Select partner title, the way the checkout sheet shows it under Find partner.
export const NEEDS_PARTNER_HINT = formatText({
  text: `This ${LEAGUE_LABELS.singularLower} is played in pairs, so this player needs a partner.`,
  role: TextRole.DescriptiveText,
});
